<template>
  <div>
    <div
      v-if="!isTextObjects"
      class="layout-sidebar-section"
    >
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'strokeAndBackgroundSection')"
      >
        <strong>Background & Stroke</strong>
        <b-icon :icon="isOpen.strokeAndBackgroundSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.strokeAndBackgroundSection">
          <div>
            <div>
              <p class="mb-2">
                Group:
              </p>
              <b-form-group
                label="Background"
                label-cols="4"
              >
                <ColorPicker
                  :value="bgColor"
                  :themes="themes"
                  copy
                  @input="onChange('bgColor', $event)"
                />
              </b-form-group>

              <b-form-group
                label="Stroke"
                label-cols="4"
              >
                <div class="d-flex">
                  <ColorPicker
                    :value="strokeColor"
                    :themes="themes"
                    copy
                    @input="onChange('strokeColor', $event)"
                  />
                  <b-button
                    v-b-tooltip.hover
                    variant="primary"
                    size="sm"
                    title="Use background color"
                    @click="onChange('strokeColor', target.fill)"
                  >
                    <b-icon icon="back" />
                  </b-button>
                </div>
              </b-form-group>

              <b-form-group
                label="Stroke width"
                :disabled="target.locked"
              >
                <b-input-group>
                  <b-input
                    :value="strokeWidth"
                    :min="0"
                    :max="maxStrokeWidth"
                    type="range"
                    @input="onChange('thickness', $event)"
                  />
                  <template #append>
                    <b-input
                      :value="strokeWidth"
                      :min="0"
                      :max="maxStrokeWidth"
                      type="number"
                      @input="onChange('thickness', $event)"
                    />
                  </template>
                </b-input-group>
              </b-form-group>
            </div>
          </div>
        </b-collapse>
      </transition>
    </div>

    <PositionAndSize
      :h="(target.height * target.scaleY)"
      :w="(target.width * target.scaleX)"
      :x="target.left"
      :y="target.top"
      :is-open="isOpen"
      @toggleSection="toggleSectionHandler"
      @input="onChange"
    />

    <div
      v-if="isTextObjects"
      class="layout-sidebar-section"
    >
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'alignmentAndOpacitySection')"
      >
        <strong>Alignment & Opacity</strong>
        <b-icon :icon="isOpen.alignmentAndOpacitySection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.alignmentAndOpacitySection">
          <div>
            <b-form-row class="mt-2">
              <b-col cols="12">
                <b-form-group label="Alignment">
                  <b-form-select
                    :value="textAlign"
                    :options="alignments"
                    @change="onChange('textAlignment', $event)"
                  >
                    <template #first>
                      <option 
                        :value="null" 
                        disabled
                      >
                        {{ textAlign === null ? 'Mixed alignment' : 'Select alignment' }}
                      </option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group label="Size">
                  <b-form-input
                    :value="fontSize"
                    type="number"
                    @input="onChange('fontSize', $event)"
                    @dblclick="selectValueOnDblclick"
                  />
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group label="Opacity">
                  <b-form-input
                    :value="opacity"
                    type="number"
                    min="0"
                    step="0.01"
                    max="1"
                    @input="onChange('opacity', $event)"
                    @dblclick="selectValueOnDblclick"
                  />
                </b-form-group>
              </b-col>
            </b-form-row>
          </div>
        </b-collapse>
      </transition>
    </div>

    <!-- Text Settings Section -->
    <div
      v-if="isTextObjects"
      class="layout-sidebar-section"
    >
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'textSettingsSection')"
      >
        <strong>Text Settings</strong>
        <b-icon :icon="isOpen.textSettingsSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.textSettingsSection">
          <div>
            <b-form-group
              v-if="themeStyles"
              label="Text Style"
            >
              <Multiselect
                v-model="selectedStyle"
                placeholder="Select style"
                track-by="id"
                label="name"
                :options="groupedThemeStyles"
                group-label="name"
                group-values="children"
                :group-select="false"
                :multi="false"
                :allow-empty="false"
                select-label=""
                deselect-label=""
                @input="applyTextStyle"
              >
                <template #option="props">
                  <span
                    v-if="!props.option.$isLabel"
                    :style="getStylePreview(props.option)"
                  >{{ getStyleOptionName(props.option) }}</span>
                  <span
                    v-else
                    :style="getStylePreview(props.option)"
                    class="themes-group-label"
                  >{{ props.option.$groupLabel }}</span>
                </template>
                <template #singleLabel="props">
                  <span
                    v-if="props.option.$isLabel"
                    :style="{ fontWeight: 'bold' }"
                  >{{ props.option.$groupLabel }}</span>
                  <span
                    v-else
                    :style="getStylePreview(props.option)"
                  >{{ getStyleOptionName(props.option) }}</span>
                </template>
              </Multiselect>
            </b-form-group>

            <b-form-group
              v-if="options.fontSelector"
              label="Font"
            >
              <Multiselect
                v-model="fontVal"
                :placeholder="fontFamily === null ? 'Mixed fonts' : 'Font'"
                track-by="value"
                label="name"
                :options="getFonts"
                :allow-empty="true"
                @select="handleFontChange"
              >
                <template #option="props">
                  <div
                    :style="{ fontFamily: props.option.value }"
                    :class="{
                      'font-used-in-theme':fontsUsedInStyles.includes(props.option.value),
                      'theme-font-separator': props.option.isLastUsed
                    }"
                  >
                    {{ props.option.name }}
                  </div>
                </template>
                <template #singleLabel="props">
                  <span :style="{ fontFamily: props.option.value }">{{ props.option.name }}</span>
                </template>
              </Multiselect>
            </b-form-group>

            <b-form-row>
              <b-col>
                <b-form-group
                  label="Size"
                  :disabled="target.locked"
                >
                  <b-form-input
                    :value="fontSize"
                    :placeholder="fontSize === null ? 'Mixed' : ''"
                    type="number"
                    min="0"
                    @input="onChange('fontSize', $event)"
                    @dblclick="selectValueOnDblclick"
                    @keypress="preventNegativeInput"
                  />
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group label="Opacity">
                  <b-form-input
                    :value="opacity"
                    :placeholder="opacity === null ? 'Mixed' : ''"
                    type="number"
                    min="0"
                    step="0.01"
                    max="1"
                    @input="onChange('opacity', $event)"
                    @dblclick="selectValueOnDblclick"
                    @keypress="preventNegativeInput"
                  />
                </b-form-group>
              </b-col>
            </b-form-row>
          </div>
        </b-collapse>
      </transition>
    </div>
    
    <div
      class="layout-sidebar-section"
    >
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'actionSection')"
      >
        <strong>Actions</strong>
        <b-icon :icon="isOpen.actionSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.actionSection">
          <div>
            <div
              class="group-actions-toolbar"
            >
              <b-btn
                variant="primary"
                @click="onChange(isCustomGroup ? 'ungroup' : 'group', null)"
              >
                {{ isCustomGroup ? 'Ungroup' : 'Group' }}
              </b-btn>

              <button
                v-b-tooltip.hover="'Center to template size'"
                class="custom-group-action"
                @click="onChangeWrapped('center', $event)"
              >
                <img
                  src="/img/icons/center-image.svg"
                  alt="center-icon"
                >
              </button>

              <button
                v-b-tooltip.hover="'Center horizontally'"
                class="custom-group-action"
                @click="onChangeWrapped('centerHorizontally', $event)"
              >
                <img
                  src="/img/icons/align-center.png"
                  alt="center-icon-horizontally"
                >
              </button>
              <button
                v-b-tooltip.hover="'Center vertically'"
                class="custom-group-action"
                @click="onChangeWrapped('centerVertically', $event)"
              >
                <img
                  src="/img/icons/vertical-alignment.png"
                  alt="center-icon-vertically"
                >
              </button>

              <SelectAllObjectsButton 
                :icon="false"
                :svg="true"
                @unselected="$emit('unselected')" 
              />
              <template v-if="isTextObjects">
                <ColorPicker
                  :value="fontColor"
                  :themes="themes"
                  position="top-start"
                  copy
                  @input="onChange('fontColor', $event)"
                />

                <DropdownCounter
                  :value="charSpacing"
                  title="Text Spacing"
                  :step="10"
                  class="group-action-dropdown"
                  @input="onChange('textSpacing', $event)"
                >
                  <i class="glyphicon glyphicon-text-width" />
                </DropdownCounter>

                <LineHeightDropdown
                  :value="lineHeight"
                  class="group-action-dropdown"
                  @input="onChange('textLeading', $event)"
                >
                  <i class="cil-line-spacing" />
                </LineHeightDropdown>
              </template>
            </div>

            <div class="position-fixed apply-to-all-text-dropdwon-section">
              <ApplyToAllTextDropdown
                v-if="isTextObjects"
                :options="options"
                :target="target"
              />

              <ApplyToAllImageDropdown
                v-else
                class="mt-3"
                :target="target"
              />
            </div>
          </div>
        </b-collapse>
      </transition>
    </div>
  
    
    <b-button
      variant="danger"
      class="w-100"
      @click="handleDelete"
    >
      <b-icon icon="trash" />
      Delete Selected Fields
    </b-button>
  </div>
</template>

<script>
/* global target, fontFamilyManager */
import PositionAndSize from "./components/PositionAndSize.vue";
import ColorPicker from './components/ColorPicker.vue'
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker'
import ApplyToAllTextDropdown
  from '@frontend/components/modules/layouts-page/EditorControl/components/ApplyToAllTextDropdown.vue';
import ApplyToAllImageDropdown
  from '@frontend/components/modules/layouts-page/EditorControl/components/ApplyToAllImageDropdown.vue';
import DropdownCounter from '@frontend/components/modules/layouts-page/EditorControl/components/DropdownCounter.vue'
import LineHeightDropdown
  from '@frontend/components/modules/layouts-page/EditorControl/components/LineHeightDropdown.vue'
import { TEXT_ALIGNMENTS } from '@frontend/constants/alignment'
import mixinInput from '@frontend/components/input-mixins'
import {fabric} from "fabric";
import {ShowPageScopeCollector} from "@frontend/group/modules/scope-collectors/show-page-collector";
import _ from "lodash";
import SelectAllObjectsButton
  from '@frontend/components/modules/layouts-page/EditorControl/components/SelectAllObjectsButton.vue'
import Multiselect from 'vue-multiselect'
import {mapGetters} from 'vuex'
import {ThemesContainerSingleton} from "@/banner_template/themes/ThemesContainerSinglton"
export default {
  components: {
    SelectAllObjectsButton,
    LineHeightDropdown,
    DropdownCounter,
    ApplyToAllImageDropdown,
    ApplyToAllTextDropdown,
    PositionAndSize,
    ColorPicker,
    Multiselect,
  },

  mixins: [
    mixinInput,
  ],

  props: {
    target: {
      type: Object,
      default: () => {
      },
    },
    options: {
      type: Object,
      default: () => {
      },
    },
    themes: {
      type: Array,
      default: () => [],
    },
    isOpen: {
      type: Object,
      default: () => ({
        actionSection: true,
        alignmentAndOpacitySection: true,
        alignmentButtonsSection: true,
        strokeAndBackgroundSection: true,
        textSettingsSection: true,
      }),
    },
  },

  data: () => ({
    maxStrokeWidth: 100,
    isCustomGroup: false,
    alignments: TEXT_ALIGNMENTS,
    strokeWidthValue: 0,
    fontVal: '',
    selectedStyle: null,
    fontFamilyWeights: [],
  }),

  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    
    isTextObjects() {
      return this.options.isTextObjects
    },

    isProductImageObjects() {
      return this.options.isProductImageObjects
    },

    isBgThemeImageObjects() {
      return this.options.isBgThemeImageObjects
    },

    bgColor() {
      const fills = this.target._objects.map(obj => obj.fill);
      return fills.every(color => color === fills[0]) ? fills[0] : null;
    },

    strokeColor() {
      const strokes = this.target._objects.map(obj => obj.stroke);
      return strokes.every(color => color === strokes[0]) ? strokes[0] : null;
    },
    strokeWidth() {
      return this.strokeWidthValue;
    },
    textAlign () {
      return this.getUniformValue('textAlign', null)
    },
    fontSize () {
      return this.getUniformValue('fontSize', null)
    },
    opacity () {
      return this.getUniformValue('opacity', null)
    },
    fontColor () {
      return this.getUniformValue('fontColor', null)
    },
    charSpacing () {
      return this.getUniformValue('charSpacing', null)
    },
    lineHeight () {
      return this.getUniformValue('lineHeight', null)
    },
    
    // Text settings computed properties
    fontFamily() {
      return this.getUniformValue('fontFamily', null)
    },
    
    getFonts () {
      const fonts = []
      const theme = ThemesContainerSingleton.getByIdSync(this.selectedInstance.data.theme);
      const allFonts = this.options.fonts;
      // getting all fonts used in theme styles
      const fontsInStyles = theme ? theme.getFonts() : [];
      const usedFontsSet = new Set(fontsInStyles);

      const usedFonts = [];
      const unusedFonts = [];

      for (const key in allFonts) {
        const font = allFonts[key];
        const selectedFontFamily = this.fontFamily;

        const fontName = Array.isArray(font)
          ? (font.includes(selectedFontFamily) ? selectedFontFamily : font[0])
          : key;

        const fontObj = {
          name: Array.isArray(font) ? key : font,
          value: fontName,
          isUsed: usedFontsSet.has(fontName),
        };

        if (fontObj.isUsed) {
          usedFonts.push(fontObj);
        } else {
          unusedFonts.push(fontObj);
        }
      }

      // Mark last used
      if (usedFonts.length) {
        usedFonts[usedFonts.length - 1].isLastUsed = true;
      }

      return [...usedFonts, ...unusedFonts];
    },
    
    themeStyles() {
      const theme = ThemesContainerSingleton.getByIdSync(this.selectedInstance.data.theme);
      let styles = theme?.attributes?.find(attr => _.get(attr, 'name') === 'Styles')?.list || [];
      if (!styles?.length && window.selectedTheme) {
        styles = window.selectedTheme?.attributes?.find(attr => _.get(attr, 'name') === 'Styles')?.list || [];
      }
      return styles;   
    },
    
    groupedThemeStyles() {
      const groups = {};
      this.themeStyles.forEach((style, index) => {
        if (!groups[style.name]) groups[style.name] = [];
        const label = this.getStyleOptionName(style);
        groups[style.name].push({ ...style, id: `${style.name}-${index}`, label });
      });

      return Object.entries(groups).map(([name, children]) => ({
        name,
        children
      }))
    },
    
    fontsUsedInStyles() {
      return this.themeStyles
        .map(style => _.get(style.list.find(attr => attr.type === 'font'), 'value'))
        .filter(font => !!font) || [];
    },
  },
  
  watch: {
    fontFamily: {
      immediate: true,
      handler(newFontFamily) {
        this.fontVal = newFontFamily ? this.findFont(newFontFamily) : null;
      }
    }
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeyDelete);
    document.addEventListener('click', this.handleOutsideClick, true);
    this.isCustomGroup = this.target?.type !== 'activeSelection';
    
    // Initialize font family weights
    if (this.fontFamily) {
      this.setFontFamily(this.fontFamily);
    }
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick, true);
    window.removeEventListener('keydown', this.handleKeyDelete);
  },
  
  methods: {
    handleOutsideClick(event) {
      const canvas = this.target?.canvas;
      const activeObject = canvas?.getActiveObject();
      const canvasElement = this.target?.canvas?.upperCanvasEl;
      const clickedInsideCanvas = canvasElement?.contains(event.target);
      if (!clickedInsideCanvas && this.target && Array.isArray(this.target._objects)) {
        const hasImage = this.target._objects.some(obj => obj instanceof fabric.Image);
        const hasText = this.target._objects.some(obj => 
          obj instanceof fabric.Text || 
          obj instanceof fabric.Textbox || 
          obj instanceof fabric.IText
        ); 
        if (!hasImage && !hasText && !activeObject) {
          this.$emit('unselected');
        }
      }
    },
    handleKeyDelete(event) {
      const activeTag = document.activeElement?.tagName;
      if (['INPUT', 'TEXTAREA'].includes(activeTag)) return;
      if (event.key === 'Delete') {
        if (this.target && this.target._objects && this.target._objects.length > 1) {
          this.handleDelete();
        }
      }
    },
    onChangeWrapped(action, event) {
      const getScopePage = () => ShowPageScopeCollector.get([
        'layoutChanges',
        'canvasHistory',
      ]);
      const {
        layoutChanges,
        canvasHistory
      } = getScopePage();
      canvasHistory.push(_.cloneDeep({
        instance_id: this.target.canvas.instance_id,
        previewChanges: layoutChanges,
        objects: this.target.canvas.getObjects()
      }));
      this.onChange(action, event);
    } ,
    onChange: _.debounce(function (action, value) {
      if (action === 'thickness') {
        const width = Number(value);
        this.strokeWidthValue = width;
        if (this.target?._objects?.length) {
          this.target._objects.forEach(obj => {
            obj.strokeWidth = width;
            obj.dirty = true;
          });
          this.target.canvas?.requestRenderAll();
        }
        return;
      }

      editorControlEventBroker.fire(EVENTS.CUSTOM_GROUP_CHANGED, {
        action,
        value,
      });
      if (action === 'group') {
        this.isCustomGroup = true;
      } else if (action === 'ungroup') {
        this.isCustomGroup = false;
      }
    }, 15),
    applyToAll([action]) {
      this.onChange(action, action === 'bgColor' ? this.target.fill : target.stroke);
    },
    toggleSectionHandler(sectionKey) {
      this.$emit('toggleSection', sectionKey);
    },
    getUniformValue (property, defaultValue = null) {
      const objects = this.target?._objects || []
      
      // Special handling for fontColor - use fill property for comparison
      if (property === 'fontColor') {
        const firstColor = this.normalizeColor(objects[0]?.fill);
        const allSameColor = objects.every(obj => {
          const objColor = this.normalizeColor(obj.fill);
          return objColor === firstColor;
        });
        return allSameColor ? firstColor : defaultValue;
      }
      
      return objects.length && objects.every(obj => obj[property] === objects[0][property])
          ? objects[0][property]
          : defaultValue
    },
 
    normalizeColor(color) {
      if (!color) return '';
      
      // Convert to string and normalize hex format
      let normalized = color.toString().toLowerCase().trim();
      
      // Handle named colors
      const namedColors = {
        'black': '#000000',
        'white': '#ffffff',
        'red': '#ff0000',
        'green': '#008000',
        'blue': '#0000ff',
        'yellow': '#ffff00',
        'orange': '#ffa500',
        'purple': '#800080',
        'pink': '#ffc0cb',
        'gray': '#808080',
        'grey': '#808080',
        'brown': '#a52a2a',
        'cyan': '#00ffff',
        'magenta': '#ff00ff',
        'lime': '#00ff00',
        'navy': '#000080',
        'teal': '#008080',
        'silver': '#c0c0c0',
        'maroon': '#800000',
        'olive': '#808000',
        'aqua': '#00ffff',
        'fuchsia': '#ff00ff'
      };
      
      if (namedColors[normalized]) {
        normalized = namedColors[normalized];
      }
      
      // Convert rgb/rgba to hex if needed
      if (normalized.startsWith('rgb')) {
        // Extract RGB values and convert to hex
        const matches = normalized.match(/\d+/g);
        if (matches && matches.length >= 3) {
          const r = parseInt(matches[0]);
          const g = parseInt(matches[1]);
          const b = parseInt(matches[2]);
          normalized = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }
      }
      
      // Normalize hex format (e.g., #000 to #000000)
      if (normalized.startsWith('#') && normalized.length === 4) {
        normalized = `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`;
      }
      
      return normalized;
    },
    async handleDelete() {
      const handler = (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          Swal.clickConfirm();
        }
      };
      const result = await Swal.fire({
        title: `<div style="text-align: left;font-size: 1.25rem; font-weight: 600; color: #1d1f22;">Delete Fields</div>`,
        keydownListenerCapture: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Delete Fields',
        reverseButtons: true,
        focusConfirm: true,
        focusCancel: false,
        showCloseButton: true,
        width: '32em',
        html: `
        <div style="text-align: left;font-size: 0.95rem; color: #4a4a4a;margin-top: 10px;">
          Are you sure you want to remove these layers from the template?
          This action cannot be undone.
        </div>
      `,
        customClass: {
          popup: 'swal2-modern-dialog delete-field-swal',
          confirmButton: 'swal2-delete-btn',
          cancelButton: 'swal2-cancel-btn',
          htmlContainer: 'swal-no-padding',
          closeButton: 'delete-field-close',
        },
        didOpen: () => {
          //remove focus from buttons
          Swal.getPopup().querySelector('button.swal2-cancel')?.blur();
          Swal.getPopup().querySelector('button.swal2-close')?.blur();
          document.addEventListener("keydown", handler);
        },
        willClose: () => {
          document.removeEventListener("keydown", handler);
        },
      });

      if (!result.isConfirmed) return false;
      
      if (result.value) {
        this.onChange('deleteGroup', null);
      }
    },
    
    // Text settings methods
    handleFontChange(font) {
      this.fontVal = font;
      this.setFontFamily(font.value);
      this.onChange('fontFamily', font.value);
    },
    
    setFontFamily(fontFamily) {
      const fontFamilies = fontFamilyManager.getFontsFamily(fontFamily);
      if (fontFamilies) {
        this.fontFamilyWeights = fontFamilies.fonts;
      } else {
        this.fontFamilyWeights = [];
      }
    },
    
    findFont(value) {
      return _.find(this.getFonts, ['value', value]);
    },
    
    applyTextStyle(style) {
      this.selectedStyle = style;
      this.onChange('applyTextStyle', style);
    },
    
    getStylePreview(style) {
      if (!style || !style.list) return {};
      
      const preview = {};
      style.list.forEach(attr => {
        if (attr.type === 'font') {
          preview.fontFamily = attr.value;
        } else if (attr.type === 'fontSize') {
          preview.fontSize = `${attr.value}px`;
        } else if (attr.type === 'fontWeight') {
          preview.fontWeight = attr.value;
        } else if (attr.type === 'color') {
          preview.color = attr.value;
        }
      });
      
      return preview;
    },
    
    getStyleOptionName(style) {
      return style.name || 'Unknown Style';
    },

    preventNegativeInput(event) {
      if (event.key === '-' || event.key === 'e') {
        event.preventDefault();
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.center-btn {
  display: inline-flex;

  img {
    width: 1.5rem;
    filter: brightness(9);
  }
}

.apply-to-all-text-dropdwon-section{
  bottom: 44px;
  z-index: 999;
}

.group-actions-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  column-gap: 0.5rem;
  row-gap: 0.25rem;
}

::v-deep .custom-group-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0.25rem;
  border: none;
  background: none;
  outline: none;
  font-size: 0.875rem;
  line-height: 1.25rem;
  cursor: pointer;
  color: #727272 !important;
}

::v-deep .group-action-dropdown .dropdown-toggle,
::v-deep .group-action-dropdown .dropdown-toggle:hover,
::v-deep .group-action-dropdown .dropdown-toggle:focus,
::v-deep .group-action-dropdown .dropdown-toggle:active,
::v-deep .group-action-dropdown.show .dropdown-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0.25rem;
  border: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
  color: #727272 !important;
}

.custom-group-action img {
  width: 20px;
}
</style>
